import * as React from "react";
import { Button, Card, Divider, Space, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../components/antd/card/CardCollapsibleContent";
import { useDrawerContext } from "../../../components/antd/drawerContext";
import { formSetValue, useCpFormValue } from "../testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpSwitch } from "../testNewForm/CpSwitch";
import { CpErrorWarning } from "../testNewForm/CpErrorWarning";
import { CpFormItem } from "../testNewForm/CpFormItem";
import { CpLabel } from "../testNewForm/CpLabel";
import { CpRadioGroup } from "../testNewForm/CpRadioGroup";
import { CpInput } from "../testNewForm/CpInput";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../envVariables";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CpKindSelect } from "../testNewForm/temp/CpKindSelect";
import { CpTooltip } from "../testNewForm/CpTooltip";
import { CpLabelTooltip } from "../testNewForm/CpLabelTooltip";
import { EmbeddedIpSetCreate } from "../../ipset/create/embedded";

interface Props {}
export const LoadBalancer: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const isEnabled = useCpFormValue(`loadBalancer.isEnabled`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Dedicated Load Balancer</CardTitle>
          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`loadBalancer.isEnabled`}>Enable Dedicated Load Balancer</CpLabel>
                <CpSwitch name={`loadBalancer.isEnabled`} />
                <CpTooltip
                  tooltipProps={{
                    title: `Creates a dedicated load balancer in each location and enables additional Domain features: custom ports,
                protocols and wildcard hostnames. Charges apply for each location.`,
                  }}
                />
              </div>
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`loadBalancer.ipSet`}>IP Set</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: (
                        <>
                          <span>Manages the locations</span>
                          <ExternalLink
                            className="ml-2"
                            label="Click here to learn more about IP Sets"
                            url={`${DOCS_URL}/reference/ipset`}
                            disableCopy
                          />
                        </>
                      ),
                    }}
                  />
                </CpLabelTooltip>
                <Space.Compact style={{ maxWidth: 600 }}>
                  <CpKindSelect
                    optionValue="name"
                    kind="ipset"
                    name={`loadBalancer.ipSet`}
                    placeholder={"Select or type an IP Set Item"}
                  />
                  <Tooltip title={`Create an IP Set`}>
                    <Button
                      onClick={() => {
                        drawer.setPersistent(
                          `gvc-loadbalancer-create-ipset`,
                          "Create Load Balancer IP Set",
                          <>
                            <EmbeddedIpSetCreate
                              onCancel={() => drawer.closePersistent()}
                              onDone={(itemLink) => {
                                drawer.closePersistent();
                                const { name } = parseLink(itemLink, { useInputCtx: true });
                                const ipSetState = {
                                  value: name,
                                  name: name,
                                  link: itemLink,
                                };
                                formSetValue(id, `loadBalancer.ipSet`, ipSetState);
                              }}
                            />
                          </>,
                          730,
                        );
                      }}
                      icon={<PlusOutlined />}
                    />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`loadBalancer.ipSet`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`loadBalancer.trustedProxies`}>Trusted Proxies</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: (
                        <div>
                          <div>
                            Controls the address used for request logging and for setting the X-Envoy-External-Address
                            header.
                          </div>
                          <div>If set to 0, then No additional external proxies are trusted.</div>
                          <div>
                            If set to 1, then the last address in an existing X-Forwarded-For header will be used in
                            place of the source client IP address.
                          </div>
                          <div>
                            <div>
                              If set to 2, then the second to last address in an existing X-Forwarded-For header will be
                              used in place of the source client IP address. If the XFF header does not have at least
                              two addresses or does not exist then the source client IP address will be used instead.
                            </div>
                          </div>
                        </div>
                      ),
                    }}
                  />
                </CpLabelTooltip>
                <CpRadioGroup
                  name={`loadBalancer.trustedProxies`}
                  options={[
                    {
                      label: "0",
                      value: "0",
                    },
                    {
                      label: "1",
                      value: "1",
                    },
                    {
                      label: "2",
                      value: "2",
                    },
                  ]}
                />
                <CpErrorWarning name={`loadBalancer.trustedProxies`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`loadBalancer.multizone.isEnabled`}>Enable Multizone</CpLabel>
                  <CpSwitch name={`loadBalancer.multizone.isEnabled`} />
                  <CpTooltip
                    tooltipProps={{
                      title: `The multi zone setting on the GVC ensures that the dedicated load balancer for that GVC will be placed
                  into all active zones in the cluster. Otherwise it may not be, depending on how many zones there are.`,
                    }}
                  />
                </div>
                <CpErrorWarning name={`loadBalancer.multizone.isEnabled`} />
              </CpFormItem>
              <Divider orientation={"left"} orientationMargin={0}>
                Redirects{" "}
                <CpTooltip
                  tooltipProps={{ title: `Specifies the url to be redirected to for different http status codes.` }}
                />
              </Divider>
              {/* TODO uri validation */}
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`loadBalancer.redirect.class.status5xx`}>Status 5xx</CpLabel>
                  <CpTooltip tooltipProps={{ title: `Specify the redirect url for any 500 level status code.` }} />
                </CpLabelTooltip>
                <CpInput name={`loadBalancer.redirect.class.status5xx`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`loadBalancer.redirect.class.status5xx`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`loadBalancer.redirect.class.status401`}>Status 401</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: (
                        <div>
                          <div>
                            An optional url redirect for 401 responses. Supports envoy format strings to include request
                            information.
                          </div>
                          <div>
                            E.g.
                            https://your-oauth-server/oauth2/authorize?return_to=%REQ(:path)%&client_id=your-client-id
                          </div>
                        </div>
                      ),
                    }}
                  />
                </CpLabelTooltip>
                <CpInput name={`loadBalancer.redirect.class.status401`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`loadBalancer.redirect.class.status401`} />
              </CpFormItem>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
